import { DialogOption } from "../../../../../lib/html5-software-library/src/ranj/eng/dialog/engine/model/Dialog";
import NGTDialogPlayer from "../../dialog/NGTDialogPlayer";
import ChatMessage from "./ChatMessage";
import ChatStatus from "./ChatStatus";

export default class ChatConversation {
    /**
     * @constructor
     * @param {string} npcId 
     */
    constructor(npcId) {
        this._npcId = npcId;

        /** @type {Array<string>} */
        this._dialogIdHistory = [];

        /** @type {Array<ChatMessage>} */
        this._messages = [];
        /** @type {Array<DialogOption>} */
        this._currentOptions = [];
        /** @type {NGTDialogPlayer} */
        this._contentPlayer = null;

        this._status = ChatStatus.OFFLINE;
    }

    get npcId() {
        return this._npcId;
    }

    get dialogIdHistory() {
        return this._dialogIdHistory;
    }

    get messages() {
        return this._messages;
    }

    get status() {
        return this._status;
    }

    set status(value) {
        this._status = value;
    }

    get currentOptions() {
        return this._currentOptions;
    }

    get contentPlayer() {
        return this._contentPlayer;
    }

    set contentPlayer(value) {
        this._contentPlayer = value;
    }

    get awaitingPlayerResponse() {
        return !!this._currentOptions;
    }

    /**
     * Get the time of the last message in the chat conversation
     * Returns 0 if the conversation has no messages
     * @returns {number}
     */
    getLastMessageTime() {
        if (this._messages.length === 0) {
            return 0;
        }

        return this._messages[this._messages.length - 1].time;
    }

    /**
     * Adds a dialog id to the history of this conversation
     * Can be to mark the dialogs as 'viewed' when the learner opens the conversation
     * @param {string} dialogId 
     */
    addDialogIdToHistory(dialogId) {
        this._dialogIdHistory.push(dialogId);
    }

    /**
     * Adds a chat message
     * @param {ChatMessage} message
     */
    addMessage(message) {
        this._messages.push(message);
    }

    /**
     * Adds options that the player can chose from to chat 
     * @param {Array<DialogOption>} options 
     */
    addOptions(options) {
        this._currentOptions = [...this._currentOptions, ...options];
    }

    /**
     * Clears all current options
     */
    clearOptions() {
        this._currentOptions = [];
    }

    /**
     * Get the amount of unread messages in this conversation
     * @returns {number} The amount of unread messages in this conversation
     */
    getUnreadCount() {
        return this.messages.reduce((prev, curr) => {
            if (!curr.isRead) {
                return prev + 1;
            }
            return prev;
        }, 0);
    }

    /**
     * Check whether the last message had been read
     * If the conversation contains no message it will return true
     * @returns {boolean}
     */
    lastMessageIsRead() {
        if (this._messages.length === 0) {
            return true;
        }

        return this._messages[this._messages.length - 1].isRead;
    }

    toObject() {
        return {
            npcId: this._npcId,
            dialogIdHistory: this._dialogIdHistory,
            messages: this._messages.map(m => m.toObject()),
            status: this._status,
        };
    }

    fromObject(obj, languagePack) {
        this._npcId = obj.npcId;
        this._dialogIdHistory = obj.dialogIdHistory;
        this._messages = obj.messages.map(m => new ChatMessage().fromObject(m, languagePack));
        this._status = obj.status;

        return this;
    }
}